import styled from '@emotion/styled'
import { color as styleColor } from '../../styles'
interface Props {
   type: 'glowing' | 'spinner' | 'loading-bar' | 'gears' | 'drops'
   color?: string
   scale?: number
   className?: string
}

const LoadingSpinner: React.FC<Props> = (props) => {
   const { type, color, scale, className } = props

   const Holder = styled.span`
      font-size: calc(1rem * ${scale || 1});
      align-self: center;
      justify-self: center;
      /* ------- */
      /* Glowing */
      /* ------- */
      &.glowing {
         position: relative;
         display: flex;
         align-items: center;
         justify-content: center;
         width: 5em;
         max-width: 6em;
         margin-top: 3em;
         margin-bottom: 3em;
         &:before,
         &:after {
            content: '';
            position: absolute;
            border-radius: 50%;
            animation: pulsOut 1.8s ease-in-out infinite;
            filter: drop-shadow(0 0 1rem color-mix(in oklab, ${color}, transparent 25%));
         }
         &:before {
            width: 100%;
            padding-bottom: 100%;
            box-shadow: inset 0 0 0 1em ${color};
            animation-name: pulsIn;
         }
         &:after {
            width: calc(100% - 2em);
            padding-bottom: calc(100% - 2em);
            box-shadow: 0 0 0 0 ${color};
         }

         @keyframes pulsIn {
            0% {
               box-shadow: inset 0 0 0 1em ${color};
               opacity: 1;
            }
            50%,
            100% {
               box-shadow: inset 0 0 0 0 ${color};
               opacity: 0;
            }
         }

         @keyframes pulsOut {
            0%,
            50% {
               box-shadow: 0 0 0 0 ${color};
               opacity: 0;
            }
            100% {
               box-shadow: 0 0 0 1em ${color};
               opacity: 1;
            }
         }
      }

      /* ------- */
      /* Spinner */
      /* ------- */
      &.spinner {
         width: 3em;
         height: 3em;
         border-radius: 50%;
         display: inline-block;
         border-top: 0.1875em solid ${color};
         border-right: 0.1875em solid transparent;
         box-sizing: border-box;
         animation: rotation 1s linear infinite;

         @keyframes rotation {
            0% {
               transform: rotate(0deg);
            }
            100% {
               transform: rotate(360deg);
            }
         }
      }

      /* ----------- */
      /* Loading Bar */
      /* ----------- */
      &.loading-bar {
         width: 6em;
         height: 1em;
         display: inline-block;
         background-color: #fff;
         border: 0.0625em solid ${color};
         border-radius: 0.25em;
         background-image: linear-gradient(
            45deg,
            ${color} 25%,
            transparent 25%,
            transparent 50%,
            ${color} 50%,
            ${color} 75%,
            transparent 75%,
            transparent
         );
         background-size: 1em 1em;
         box-sizing: border-box;
         animation: barStripe 1s linear infinite;

         @keyframes barStripe {
            0% {
               background-position: 1em 0;
            }
            100% {
               background-position: 0 0;
            }
         }
      }

      /* ----- */
      /* Gears */
      /* ----- */
      &.gears {
         transform: scale(${scale || 1});
         width: 60px;
         height: 40px;
         position: relative;
         display: inline-block;
         --base-color: ${styleColor.white}; /*use your base color*/

         &::before {
            content: '';
            left: 0;
            top: 0;
            position: absolute;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background-color: ${color};
            background-image: radial-gradient(
                  circle 8px at 18px 18px,
                  var(--base-color) 100%,
                  transparent 0
               ),
               radial-gradient(circle 4px at 18px 0px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 0px 18px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 36px 18px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 18px 36px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 30px 5px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 30px 5px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 30px 30px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 5px 30px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 4px at 5px 5px, var(--base-color) 100%, transparent 0);
            background-repeat: no-repeat;
            box-sizing: border-box;
            animation: rotationBack 5s linear infinite;
         }
         &::after {
            content: '';
            left: 35px;
            top: 15px;
            position: absolute;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background-color: ${color};
            background-image: radial-gradient(
                  circle 5px at 12px 12px,
                  var(--base-color) 100%,
                  transparent 0
               ),
               radial-gradient(circle 2.5px at 12px 0px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 0px 12px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 24px 12px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 12px 24px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 20px 3px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 20px 3px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 20px 20px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 3px 20px, var(--base-color) 100%, transparent 0),
               radial-gradient(circle 2.5px at 3px 3px, var(--base-color) 100%, transparent 0);
            background-repeat: no-repeat;
            box-sizing: border-box;
            animation: rotationBack 2.5s linear infinite reverse;
         }
         @keyframes rotationBack {
            0% {
               transform: rotate(0deg);
            }
            100% {
               transform: rotate(-360deg);
            }
         }
      }

      /* ----- */
      /* Drops */
      /* ----- */
      &.drops {
         width: 3em;
         height: 3em;
         border: 0.3125em solid ${color};
         border-radius: 50%;
         display: inline-block;
         box-sizing: border-box;
         position: relative;
         animation: pulse 1s linear infinite;

         &:after {
            content: '';
            position: absolute;
            width: 3em;
            height: 3em;
            border: 0.3125em solid ${color};
            border-radius: 50%;
            display: inline-block;
            box-sizing: border-box;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            animation: scaleUp 1s linear infinite;
         }

         @keyframes scaleUp {
            0% {
               transform: translate(-50%, -50%) scale(0);
            }
            60%,
            100% {
               transform: translate(-50%, -50%) scale(1);
            }
         }
         @keyframes pulse {
            0%,
            60%,
            100% {
               transform: scale(1);
            }
            80% {
               transform: scale(1.2);
            }
         }
      }
   `
   return <Holder className={`${type} ${className || ''}`}></Holder>
}

export default LoadingSpinner
